import { useEffect } from 'react';
import { Button, ConfigProvider, Dropdown, Menu, MenuProps } from 'antd';
import './App.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import PhotoGallery from './PhotoGallery';
import { NavLink, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import HowToAdoptAPainting from './HowToAdoptAPainting';
import FAQs from './FAQs';
import WhyAdopt from './WhyAdopt';
import AfterAdoption from './AfterAdoption';
import LandingPage from './LandingPage';
import Biography from './Biography';
import { areAdoptionsOpen, getIsMobile, reportAnalytics } from './utils';
import Pricing from './Pricing';
import { CARE_AND_CONSERVATION_KEY } from './constants';
import TextPage from './TextPage';
import { Header } from 'antd/es/layout/layout';
import AppFooter from './AppFooter';
import { MenuOutlined } from '@ant-design/icons';
import AdoptionsAreCurrentlyClosed from './AdoptionsAreCurrentlyClosed';
import ContactUsModal from './ContactUsModal';
import ArchiveLandingPage from './ArchiveLandingPage';
import ArchiveGallery from './ArchiveGallery';
import CurrentShow from './CurrentShow';
import DecadeGallery from './DecadeGallery';
import SearchWorksPage from './SearchWorksPage';
import ContactPage from './ContactPage';
import AdminPage from './AdminPage';
import ShopPage from './ShopPage';
import TermsAndConditionsModal from './TermsAndConditionsModal';
import UserGuidePage from './UserGuidePage';
import ArchiveBiographyPage from './ArchiveBiographyPage';

const queryClient = new QueryClient();

function AppInner() {
  return (
    <div>
      <div className="App">
          <Routes>
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/adoption-project" element={<AdoptionLayout />}>
              <Route index element={<Navigate to="home" />} />
              <Route path="home" element={<LandingPage />} />
              <Route path="gallery" element={<PhotoGallery />} />
              <Route path="about" element={<Biography />} />
              <Route path="why-adopt" element={<WhyAdopt />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="adopt" element={<HowToAdoptAPainting />} />
              <Route path="faqs" element={<FAQs />} />
              <Route path="after-adoption" element={<AfterAdoption />} />
              <Route path="art-conservators" element={<TextPage textKey={CARE_AND_CONSERVATION_KEY} />} />
              <Route path="*" element={<Navigate replace to="/" />} />
            </Route>
            <Route path="/" element={<ArchiveLayout />}>
              <Route index element={<ArchiveLandingPage />} />
              <Route path="gallery">
                <Route index element={<ArchiveGallery />} />
                <Route path=":decade" element={<DecadeGallery />} />
              </Route>
              <Route path="search" element={<SearchWorksPage />} />
              <Route path="current-show" element={<CurrentShow />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="shop" element={<ShopPage />} />
              <Route path="about" element={<Biography isArchive={true}/>} />
              <Route path="biography" element={<ArchiveBiographyPage />} />
              <Route path="user-guide" element={<UserGuidePage />} />
              <Route path="*" element={<Navigate replace to="/" />} />
            </Route>
          </Routes>
      </div>
    </div>
  );
}

function AdoptionLayout() {
  const location = useLocation();
  const selectedKey = location.pathname.split('/')[1];

  useEffect(() => {
    if (location.search.length === 0) {
      reportAnalytics('view_page', { page: selectedKey });
    }
  }, [selectedKey, location.search.length]);

  const headerElem = (
    <Header className="bg-[#193259] text-white text-center justify-center flex flex-col text-3xl p-[8px] h-fit mb-6">
      <div className="pt-2 pb-1">Gordaneer Painting Adoption Project</div>
      {!areAdoptionsOpen() && (
        <div className="text-sm flex justify-center">
          <AdoptionsAreCurrentlyClosed />
        </div>
      )}
    </Header>
  );

  // Menu needs different layout for mobile
  const isMobile = getIsMobile();

  const menuStyle = isMobile ?
    { maxWidth: '60px', flex: 'auto', marginBottom: '1.5rem', backgroundColor: '#193259' } : { width: 'fit-content' }

  return (
    <div className="min-h-svh flex flex-col items-stretch">
      <ContactUsModal />
      <div className="grow">
        {
          !isMobile && headerElem
        }
        <div className="box wrapper">
          <div className="box sidebar">
            <Menu
              mode={isMobile ? 'horizontal' : 'vertical'}
              style={menuStyle}
              defaultSelectedKeys={[selectedKey]}
              selectedKeys={[selectedKey]}
              overflowedIndicator={isMobile ? <MenuOutlined /> : null}
            >
              <Menu.Item key="home" title="Home"><NavLink to="/adoption-project/home">Home</NavLink></Menu.Item>
              <Menu.Item key="gallery" title="Gallery"><NavLink to="/adoption-project/gallery">Gallery</NavLink></Menu.Item>
              <Menu.Item key="about" title="Biography"><NavLink to="/adoption-project/about">Biography</NavLink></Menu.Item>
              <Menu.Item key="why-adopt" title="About this Project"><NavLink to="/adoption-project/why-adopt">About this Project</NavLink></Menu.Item>
              <Menu.Item key="pricing" title="Pricing"><NavLink to="/adoption-project/pricing">Pricing</NavLink></Menu.Item>
              <Menu.Item key="adopt" title="Adopt a Painting"><NavLink to="/adoption-project/adopt">Adopt a Painting</NavLink></Menu.Item>
              <Menu.Item key="after-adoption" title="After Adoption"><NavLink to="/adoption-project/after-adoption">After Adoption</NavLink></Menu.Item>
              <Menu.Item key="art-conservators" title="Care & Conservation"><NavLink to="/adoption-project/art-conservators">Care & Conservation</NavLink></Menu.Item>
              <Menu.Item key="faqs" title="FAQs"><NavLink to="/adoption-project/faqs">FAQs</NavLink></Menu.Item>
            </Menu>
            {isMobile && <div className="w-full">{headerElem}</div>}
          </div>
          <div className="box content">
            <Outlet />
          </div>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}

function ArchiveLayout() {
  const location = useLocation();
  const selectedKey = location.pathname.split('/').filter(s => s !== '').slice(0, 1).join("/");

  useEffect(() => {
    if (location.search.length === 0) {
      reportAnalytics('view_page', { page: selectedKey });
    }
  }, [selectedKey, location.search.length]);

  const mobileNavItems: MenuProps['items'] = [
    { key: '', label: <NavLink to="/">Home</NavLink> },
    { key: 'biography', label: <NavLink to="/biography">Biography</NavLink> },
    { key: 'user-guide', label: <NavLink to="/user-guide">User Guide</NavLink> },
    { key: 'gallery', label: <NavLink to="/gallery">Works by Decade</NavLink> },
    { key: 'search', label: <NavLink to="/search">All Works</NavLink> },
    { key: 'current-show', label: <NavLink to="/current-show">Current Show</NavLink> },
    { key: 'shop', label: <NavLink to="/shop">Shop</NavLink> },
    { key: 'contact', label: <NavLink to="/contact">Get in Touch</NavLink> },
  ];

  return (
    <div className="min-h-svh flex flex-col items-stretch">
      <TermsAndConditionsModal />
      <ContactUsModal />
      <div className="grow">
        <div className="">
          <div className="lg:pl-20">
            <Header className="bg-white flex flex-row items-center justify-between text-2xl lg:text-4xl pt-4 lg:pt-10 pb-3 lg:pb-5 px-4 h-fit">
              <div className="font-light">
                James Gordaneer, RCA
              </div>
              {/* Mobile hamburger - hidden on desktop */}
              <div className="md:hidden">
                <Dropdown menu={{ items: mobileNavItems, selectedKeys: [selectedKey] }} trigger={['click']}>
                  <Button icon={<MenuOutlined />} className="mobile-menu-btn">Menu</Button>
                </Dropdown>
              </div>
            </Header>
            {/* Desktop nav */}
            <div className="hidden md:block">
              <Menu
                mode="horizontal"
                defaultSelectedKeys={[selectedKey]}
                selectedKeys={[selectedKey]}
                className="archive-main-menu"
              >
                <Menu.Item key="" title="Home"><NavLink to="/">Home</NavLink></Menu.Item>
                <Menu.Item key="biography" title="Biography"><NavLink to="/biography">Biography</NavLink></Menu.Item>
                <Menu.Item key="user-guide" title="User Guide"><NavLink to="/user-guide">User Guide</NavLink></Menu.Item>
                <Menu.Item key="gallery" title="Works by Decade"><NavLink to="/gallery">Works by Decade</NavLink></Menu.Item>
                <Menu.Item key="search" title="All Works"><NavLink to="/search">All Works</NavLink></Menu.Item>
                <Menu.Item key="current-show" title="Current Show"><NavLink to="/current-show">Current Show</NavLink></Menu.Item>
                <Menu.Item key="shop" title="Shop"><NavLink to="/shop">Shop</NavLink></Menu.Item>
                <Menu.Item key="contact" title="Get in Touch"><NavLink to="/contact">Get in Touch</NavLink></Menu.Item>
              </Menu>
            </div>
          </div>
          <div className="box content">
            <Outlet />
          </div>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          components: {
            Menu: {
              activeBarHeight: 0, // This removes the highlight bar on the menu when its horizontal on mobile
            },
          },
        }}
      >
        <AppInner />
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;